class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll(
      '#cart-icon-bubble, .desktop-pill-navbar__actions a[href*="/cart"], .desktop-pill-navbar__mobile-actions a[href*="/cart"]'
    );
    if (!cartLinks.length) return;

    cartLinks.forEach((cartLink) => {
      if (cartLink.dataset.drawerBound === 'true') return;
      cartLink.dataset.drawerBound = 'true';

      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (window.location.pathname === '/cart') return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const emptyContainer = this.querySelector('.drawer__inner-empty');
        const containerToTrapFocusOn =
          this.classList.contains('is-empty') && emptyContainer ? emptyContainer : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner) drawerInner.classList.toggle('is-empty', !parsedState.item_count);
    this.classList.toggle('is-empty', !parsedState.item_count);
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      if (!parsedState.sections || !parsedState.sections[section.id]) return;
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const sourceInnerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (sourceInnerHTML == null) return;
      sectionElement.innerHTML = sourceInnerHTML;
    });

    syncNavbarCartBadges(getCartItemCountFromDrawer(parsedState));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return source ? source.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function getCartItemCountFromDrawer(parsedState) {
  if (parsedState && typeof parsedState.item_count === 'number') return parsedState.item_count;

  const countLabel = document.querySelector('.custom-ajax-cart__count');
  if (!countLabel) return null;
  const parsed = parseInt((countLabel.textContent || '').replace(/\D+/g, ''), 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function syncNavbarCartBadges(itemCount) {
  if (typeof itemCount !== 'number' || Number.isNaN(itemCount)) return;

  const cartLinks = document.querySelectorAll(
    '.desktop-pill-navbar__actions a[href*="/cart"], .desktop-pill-navbar__mobile-actions a[href*="/cart"]'
  );

  cartLinks.forEach((link) => {
    const existingBadge = link.querySelector('.desktop-pill-navbar__badge');
    if (itemCount > 0) {
      if (existingBadge) {
        existingBadge.textContent = String(itemCount);
      } else {
        const badge = document.createElement('span');
        badge.className = 'desktop-pill-navbar__badge';
        badge.textContent = String(itemCount);
        link.appendChild(badge);
      }
    } else if (existingBadge) {
      existingBadge.remove();
    }
  });
}

window.syncNavbarCartBadges = syncNavbarCartBadges;

document.addEventListener('click', (event) => {
  const cartLink = event.target.closest(
    '#cart-icon-bubble, .desktop-pill-navbar__actions a[href*="/cart"], .desktop-pill-navbar__mobile-actions a[href*="/cart"]'
  );
  if (!cartLink) return;
  if (window.location.pathname === routes.cart_url) return;

  const drawer = document.querySelector('cart-drawer');
  if (!drawer || typeof drawer.open !== 'function') return;

  event.preventDefault();
  drawer.open(cartLink);
});

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
  if (!target.matches('[data-cart-attribute-input]')) return;

  const attributeKey = target.dataset.attributeKey;
  if (!attributeKey) return;

  const attributeValue =
    target instanceof HTMLInputElement && target.type === 'checkbox'
      ? target.checked
        ? target.value || 'Yes'
        : ''
      : target.value;

  const body = JSON.stringify({
    attributes: {
      [attributeKey]: attributeValue,
    },
  });

  fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } }).catch((error) => {
    console.error('Cart attribute update failed', error);
  });
});
